import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button, Card, Input, Label } from '../design-system';
import { 
  getInvitationByToken, 
  acceptInvitation, 
  getPlatformPolicies,
  InvitationDetails,
  PlatformPolicy,
} from '../services/consentApi';
import '../design-system/styles.css';

type PageState = 'loading' | 'valid' | 'invalid' | 'expired' | 'accepted' | 'error';

/**
 * Accept Invitation page.
 * Users land here from the invitation email link.
 * 
 * URL: /invite/accept?token=<invitation-token>
 */
export const AcceptInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [pageState, setPageState] = useState<PageState>('loading');
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_policies, setPolicies] = useState<PlatformPolicy[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Password validation
  const passwordValid = password.length >= 8;
  const passwordsMatch = password === confirmPassword;

  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      setError('No invitation token provided');
      return;
    }

    const loadData = async () => {
      try {
        // Load invitation details and policies in parallel
        const [invitationData, policiesData] = await Promise.all([
          getInvitationByToken(token),
          getPlatformPolicies().catch(() => []), // Policies optional
        ]);

        setInvitation(invitationData);
        setPolicies(policiesData);

        // Check invitation status
        if (invitationData.invitation.status === 'ACCEPTED') {
          setPageState('accepted');
        } else if (invitationData.invitation.status === 'EXPIRED') {
          setPageState('expired');
        } else if (invitationData.invitation.status === 'REVOKED') {
          setPageState('invalid');
          setError('This invitation has been revoked');
        } else {
          setPageState('valid');
          // Pre-fill name if provided
          if (invitationData.invitation.name) {
            setName(invitationData.invitation.name);
          }
        }
      } catch (err: any) {
        console.error('Failed to load invitation', err);
        setPageState('invalid');
        setError(err.response?.data?.error || 'Invalid or expired invitation');
      }
    };

    loadData();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token || !passwordValid || !passwordsMatch) return;
    if (!privacyAccepted || !termsAccepted) {
      setError('You must accept the Privacy Policy and Terms of Service');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const result = await acceptInvitation({
        token,
        password,
        name: name || undefined,
        privacyPolicyAccepted: privacyAccepted,
        termsOfServiceAccepted: termsAccepted,
      });

      // Redirect to login with success message
      const loginUrl = result.tenantSlug 
        ? `/t/${result.tenantSlug}/login` 
        : '/login';
      
      navigate(loginUrl, { 
        state: { 
          message: 'Account created successfully! Please sign in.',
          email: invitation?.invitation.email,
        } 
      });
    } catch (err: any) {
      console.error('Failed to accept invitation', err);
      setError(err.response?.data?.error || 'Failed to create account');
      setSubmitting(false);
    }
  };

  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'ADMIN': return 'Administrator with full access to all features';
      case 'CONSENT_MANAGER': return 'Consent Manager who can manage templates and view records';
      case 'VIEWER': return 'Viewer with read-only access';
      default: return role;
    }
  };

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="ds-auth-page">
        <div className="ds-auth-container">
          <Card className="ds-auth-card">
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div className="ds-spinner" />
              <p className="ds-text-muted" style={{ marginTop: 16 }}>Loading invitation...</p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Invalid/Error state
  if (pageState === 'invalid') {
    return (
      <div className="ds-auth-page">
        <div className="ds-auth-container">
          <Card className="ds-auth-card">
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
              <h2 className="ds-heading-lg">Invalid Invitation</h2>
              <p className="ds-text-muted" style={{ marginTop: 8, marginBottom: 24 }}>
                {error || 'This invitation link is invalid or has expired.'}
              </p>
              <Button variant="primary" onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Expired state
  if (pageState === 'expired') {
    return (
      <div className="ds-auth-page">
        <div className="ds-auth-container">
          <Card className="ds-auth-card">
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>⏰</div>
              <h2 className="ds-heading-lg">Invitation Expired</h2>
              <p className="ds-text-muted" style={{ marginTop: 8, marginBottom: 24 }}>
                This invitation has expired. Please contact your administrator to send a new invitation.
              </p>
              <Button variant="primary" onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Already accepted state
  if (pageState === 'accepted') {
    return (
      <div className="ds-auth-page">
        <div className="ds-auth-container">
          <Card className="ds-auth-card">
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
              <h2 className="ds-heading-lg">Already Accepted</h2>
              <p className="ds-text-muted" style={{ marginTop: 8, marginBottom: 24 }}>
                This invitation has already been accepted. You can sign in to your account.
              </p>
              <Button 
                variant="primary" 
                onClick={() => navigate(invitation?.tenantSlug ? `/t/${invitation.tenantSlug}/login` : '/login')}
              >
                Go to Login
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Valid invitation - show form
  return (
    <div className="ds-auth-page">
      <div className="ds-auth-container">
        <Card className="ds-auth-card" style={{ maxWidth: 480 }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <h1 className="ds-heading-xl">Join {invitation?.tenantName}</h1>
            <p className="ds-text-muted">
              You've been invited to join as <strong>{invitation?.invitation.role.replace('_', ' ')}</strong>
            </p>
          </div>

          {/* Invitation Info */}
          <div style={{ 
            background: 'var(--ds-surface)', 
            padding: 16, 
            borderRadius: 'var(--ds-radius-md)',
            marginBottom: 24,
          }}>
            <div style={{ display: 'grid', gap: 8 }}>
              <div>
                <span className="ds-text-muted" style={{ fontSize: 12 }}>Email</span>
                <div style={{ fontWeight: 500 }}>{invitation?.invitation.email}</div>
              </div>
              <div>
                <span className="ds-text-muted" style={{ fontSize: 12 }}>Your Role</span>
                <div style={{ fontWeight: 500 }}>{getRoleDescription(invitation?.invitation.role || '')}</div>
              </div>
              <div>
                <span className="ds-text-muted" style={{ fontSize: 12 }}>Invited By</span>
                <div style={{ fontWeight: 500 }}>{invitation?.invitation.invitedByName || 'Administrator'}</div>
              </div>
            </div>
          </div>

          {error && (
            <div className="ds-alert ds-alert--error" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Name */}
            <div className="ds-field" style={{ marginBottom: 16 }}>
              <Label htmlFor="name">Your Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setName(e.target.value)}
                placeholder="Enter your full name"
              />
            </div>

            {/* Password */}
            <div className="ds-field" style={{ marginBottom: 16 }}>
              <Label htmlFor="password">Create Password *</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                required
              />
              {password && !passwordValid && (
                <span style={{ color: 'var(--ds-error)', fontSize: 12 }}>
                  Password must be at least 8 characters
                </span>
              )}
            </div>

            {/* Confirm Password */}
            <div className="ds-field" style={{ marginBottom: 24 }}>
              <Label htmlFor="confirmPassword">Confirm Password *</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                required
              />
              {confirmPassword && !passwordsMatch && (
                <span style={{ color: 'var(--ds-error)', fontSize: 12 }}>
                  Passwords do not match
                </span>
              )}
            </div>

            {/* Platform Consent */}
            <div style={{ 
              background: 'var(--ds-surface)', 
              padding: 16, 
              borderRadius: 'var(--ds-radius-md)',
              marginBottom: 24,
            }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                Terms & Conditions
              </h3>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={privacyAccepted}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPrivacyAccepted(e.target.checked)}
                    style={{ marginTop: 4 }}
                  />
                  <span style={{ fontSize: 14 }}>
                    I have read and agree to the{' '}
                    <a 
                      href="/privacy-policy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: 'var(--ds-primary)' }}
                    >
                      Privacy Policy
                    </a>
                  </span>
                </label>
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTermsAccepted(e.target.checked)}
                    style={{ marginTop: 4 }}
                  />
                  <span style={{ fontSize: 14 }}>
                    I have read and agree to the{' '}
                    <a 
                      href="/terms-of-service" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: 'var(--ds-primary)' }}
                    >
                      Terms of Service
                    </a>
                  </span>
                </label>
              </div>
            </div>

            <Button
              type="submit"
              variant="primary"
              disabled={submitting || !passwordValid || !passwordsMatch || !privacyAccepted || !termsAccepted}
              isLoading={submitting}
              style={{ width: '100%' }}
            >
              Create Account & Join
            </Button>
          </form>

          <p className="ds-text-muted" style={{ textAlign: 'center', marginTop: 24, fontSize: 13 }}>
            Already have an account?{' '}
            <a 
              href={invitation?.tenantSlug ? `/t/${invitation.tenantSlug}/login` : '/login'}
              style={{ color: 'var(--ds-primary)' }}
            >
              Sign in
            </a>
          </p>
        </Card>
      </div>
    </div>
  );
};

export default AcceptInvitation;
